import { PatientInactiveBadge, PatientStatusBadge } from "../components/AdministrationFields";
import { PatientVerticalView } from "../components/PatientVerticalView";
import { formatPatientName } from "../model/administrationRules";
import { emptyStyle } from "../styles/administrationModalStyles";
import {
    dirtyDotStyle,
    eyebrowStyle,
    miniAvatarStatusStyle,
    noSelectionStyle,
    patientListBodyStyle,
    patientListHeaderStyle,
    patientListItemStatusStyle,
    patientListNameWithBadgeStyle,
    verticalSidebarStyle,
    verticalWorkspaceStyle,
} from "../styles/administrationStyles";

// Ligne de la liste : initiales, nom, badges, chambre et pastille de brouillon.
function ResidentListItem({ row, current, active, dirty, onSelect }) {
    return (
        <button
            type="button"
            aria-pressed={active}
            onClick={onSelect}
            style={patientListItemStatusStyle({ active, inactive: current.inactive, notInvoiced: current.notInvoiced })}
        >
            <span style={miniAvatarStatusStyle({ active, inactive: current.inactive, notInvoiced: current.notInvoiced })}>
                {`${row.prenom?.[0] || ""}${row.nom?.[0] || ""}`.toUpperCase()}
            </span>
            <span style={{ minWidth: 0, flex: 1 }}>
                <span style={patientListNameWithBadgeStyle}>
                    <strong style={{ color: current.inactive ? "#1e3a8a" : active ? "#312e81" : "#253047", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {formatPatientName(current)}
                    </strong>
                    <PatientStatusBadge status={current.patientStatus} />
                    <PatientInactiveBadge inactive={current.inactive} />
                </span>
                <small style={{ color: current.inactive ? "#1d4ed8" : "#8b94a5" }}>
                    {current.chambre ? `Chambre ${current.chambre}` : "Chambre non renseignée"}
                    {current.productionSubcategory ? ` · ${current.productionSubcategory}` : ""}
                </small>
            </span>
            {dirty && <span title="Modifications non enregistrées" style={dirtyDotStyle} />}
        </button>
    );
}

// Fiche du résident sélectionné, une fois ses événements et vaccinations chargés.
function ResidentDetail({ view }) {
    const { archivePdaComment, cancelRow, deletePatientDocument, detailLoading, documentBusyIds, loading, openPatientDocument, pdaHistoryRefreshKey, refreshEventPatient, saveRow, selectedCurrent, selectedDirty, selectedEhpadSubcategories, selectedHasDetails, selectedRow, selectedSaving, setBillingHistoryRow, setChronicProductRow, setDocumentRow, setEventsModal, setHistoryRow, setIdentityRow, setRobotHistoryRow, updateRowDraft, uploadPatientDocument, user } = view;
    return (
        <>
        {(loading || detailLoading || (selectedRow && !selectedHasDetails)) && <p style={emptyStyle}>Chargement…</p>}
        {!loading && !detailLoading && selectedRow && selectedCurrent && selectedHasDetails && (
            <PatientVerticalView
                row={selectedRow}
                current={selectedCurrent}
                dirty={selectedDirty}
                saving={selectedSaving}
                onPatch={patch => updateRowDraft(selectedRow, patch)}
                onSave={() => saveRow(selectedRow.patientId)}
                onCancel={() => cancelRow(selectedRow.patientId)}
                onHistory={() => setHistoryRow(selectedRow)}
                onIdentity={() => setIdentityRow(selectedCurrent)}
                onDocuments={() => setDocumentRow(selectedCurrent)}
                onEvent={(eventRow, type, vaccination) => setEventsModal({ row: eventRow, type, vaccination })}
                onOpenChronicProducts={eventRow => setChronicProductRow(eventRow)}
                onOpenBillingHistory={eventRow => setBillingHistoryRow(eventRow)}
                onRefreshBilling={refreshEventPatient}
                user={user}
                onUploadDocument={file => uploadPatientDocument(selectedRow.patientId, file)}
                onOpenDocument={document => openPatientDocument(selectedRow.patientId, document)}
                onDeleteDocument={document => deletePatientDocument(selectedRow.patientId, document)}
                documentBusy={Boolean(documentBusyIds[selectedRow.patientId])}
                onRobotHistory={() => setRobotHistoryRow(selectedRow)}
                onArchivePdaComment={archivePdaComment}
                pdaHistoryRefreshKey={pdaHistoryRefreshKey}
                productionSubcategories={selectedEhpadSubcategories}
            />
        )}
        {!loading && !detailLoading && !selectedRow && (
            <div style={noSelectionStyle}>
                <h2 style={{ marginBottom: "6px" }}>Aucune fiche sélectionnée</h2>
                <p style={{ margin: 0, color: "#7b8494" }}>Choisissez un résident dans la liste pour afficher son suivi.</p>
            </div>
        )}
        </>
    );
}

export function renderVerticalWorkspace(view) {
    const { drafts, filteredRows, handlePatientsScroll, loading, loadingMorePatients, selectedEhpadName, selectedPatientId, setSelectedPatientId } = view;
    return (
    <div className="administration-vertical-workspace" style={verticalWorkspaceStyle}>
        <aside className="administration-vertical-sidebar" style={verticalSidebarStyle}>
            <div style={patientListHeaderStyle}>
                <span style={eyebrowStyle}>Résidents</span>
                <strong style={{ color: "#172033" }}>{filteredRows.length} fiche(s)</strong>
            </div>
            <div data-testid="administration-patient-list" style={patientListBodyStyle} onScroll={handlePatientsScroll}>
                {loading && <p style={emptyStyle}>Chargement…</p>}
                {!loading && filteredRows.length === 0 && <p style={emptyStyle}>Aucun résident trouvé.</p>}
                {!loading && filteredRows.map(row => (
                    <ResidentListItem
                        key={row.patientId}
                        row={row}
                        current={drafts[row.patientId] || row}
                        active={String(row.patientId) === String(selectedPatientId)}
                        dirty={Boolean(drafts[row.patientId])}
                        onSelect={() => setSelectedPatientId(String(row.patientId))}
                    />
                ))}
                {!loading && loadingMorePatients && <p style={emptyStyle}>Chargement des résidents suivants…</p>}
            </div>
            <span style={{ color: "#64748b", fontSize: "12px", padding: "0 14px 14px" }}>{selectedEhpadName || "Aucun EHPAD sélectionné"}</span>
        </aside>
        <main style={{ minWidth: 0 }}>
            <ResidentDetail view={view} />
        </main>
    </div>
    );
}
